import React from 'react';

/**
 * Generate paragraph layout from text string
 * @param {string} str - text string
 * @param {string} className - class name to be assigned to each paragraph
 * @returns {JSX} JSX element which contains generated paragraph layout
 */
export const createParagraph = (str, className) => {
	const paragraphs = str.split('\n');
	const reactParagraphs = paragraphs.map((paragraph, index) =>
		React.createElement('p', { className, key: 'p' + index }, paragraph));

	return React.createElement(React.Fragment, {}, reactParagraphs);
};

/**
 * Returns text or formatted null value
 * @param {string | null} value - text string or null value
 * @returns {string} formatted value
 */
export const formatNullableText = value => {
	return null === value ? '-' : value;
};

/**
 * Format date string
 * @param {string} dateStr - date string
 * @param {function} translate - translate function
 * @param {boolean} showTime - include time value
 * @param {boolean} showSeconds - include seconds in time value
 * @returns {string} formatted date string
 */
export const formatDate = (dateStr, translate, showTime = false, showSeconds = true) => {
	const months = [
		'jan',
		'feb',
		'mar',
		'apr',
		'may',
		'jun',
		'jul',
		'aug',
		'sep',
		'oct',
		'nov',
		'dec'
	];

	const addZero = num => {
		return (0 <= num && 10 > num) ? '0' + num : num + '';
	};

	const dateObj = new Date(dateStr);
	const seconds = addZero(dateObj.getSeconds());
	const minutes = addZero(dateObj.getMinutes());
	const hour = addZero(dateObj.getHours());
	const month = 'function' === typeof translate
		? translate('month_' + months[dateObj.getMonth()])
		: months[dateObj.getMonth()];
	const day = dateObj.getDate();
	const year = dateObj.getFullYear();

	let formattedDate = `${month} ${day}, ${year}`;

	formattedDate += showTime ? ` ${hour}:${minutes}` : '';
	formattedDate += showTime && showSeconds ? `:${seconds}` : '';

	return formattedDate;
};

/**
 * Converts number to shorter nonation. Ex. "1230000" => "1.23M"
 * @param {number} num - numeric value
 * @returns {string} shorter string number string
 */
export const numberToShortString = num => {
	const value = num.toString().replace(/[^0-9.]/g, '');

	if (1000 > value) 
		return '' + value;
	
	let si = [
		{ v: 1E3, s: 'K' },
		{ v: 1E6, s: 'M' }
	];

	let index;
	for (index = si.length - 1; 0 < index; --index) {
		if (value >= si[index].v) 
			break;
		
	}

	return (value / si[index].v).toFixed(2).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[index].s;
};

/**
 * Sets a dalayed function call
 * @param {function} callback - callback function
 * @param {number} delay - the time, in milliseconds that the timer should wait before callback is executed.
 * @param {number} timer - previous timer number
 * @returns {number} timer number
 */
export const delayedCall = (callback, delay, timer) => {
	if (timer)
		clearTimeout(timer);

	return setTimeout(callback, delay);
};

export const getAngle = (position1, position2) => {
	const diffX = position1.x - position2.x;
	const diffY = position1.y - position2.y;
	let atan = Math.atan2(diffY, diffX) * 180 / Math.PI + 180;

	return Math.round(atan);
}

export const clampAngle = (angle, min, max) => {
	return angle > max ? max : angle < min ? min : angle;
} 
